import React, { useState } from 'react';
import { changePassword } from '@/api/platform/settings';

interface ChangePasswordFormProps {
  /** 是否显示「修改密码」标题与说明（弹框场景由弹框自带标题时可关闭） */
  showTitle?: boolean;
  /** 密码更新成功后的回调（如强制改密弹框中退出重新登录） */
  onSuccess?: () => void;
}

/** 修改密码表单：安全设置与强制改密弹框共用 */
const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({ showTitle = true, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [form, setForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleSave = async () => {
    setError('');
    if (!form.currentPassword) { setError('请输入当前密码'); return; }
    if (form.newPassword.length < 8 || form.newPassword.length > 16) { setError('新密码长度须为8至16位'); return; }
    if (form.newPassword === form.currentPassword) { setError('新密码不能与旧密码相同'); return; }
    if (form.newPassword !== form.confirmPassword) { setError('两次输入的密码不一致'); return; }
    setLoading(true);
    try {
      await changePassword({
        oldPassword: form.currentPassword,
        newPassword: form.newPassword,
      });
      setSaved(true);
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      if (onSuccess) {
        onSuccess();
      } else {
        setTimeout(() => setSaved(false), 2500);
      }
    } catch (err: unknown) {
      console.warn('Change password failed:', err);
      setError(String(err).split('Error:')[1]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {showTitle && (
        <>
          <h3 className="font-heading font-bold text-base text-foreground-950 mb-1">修改密码</h3>
          <p className="text-xs text-foreground-500 mb-5">请确保密码长度为 8 至 16 位，必须包含大小写字母、数字和特殊字符</p>
        </>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-xs font-medium text-foreground-600 mb-1.5">当前密码</label>
          <div className="relative">
            <input
              type={showCurrent ? 'text' : 'password'}
              maxLength={50}
              placeholder="当前密码"
              value={form.currentPassword}
              onChange={(e) => setForm((f) => ({ ...f, currentPassword: e.target.value }))}
              className="w-full px-3 py-2.5 pr-10 text-sm rounded-xl bg-background-50 border border-background-200/60 text-foreground-900 focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-300 transition-colors"
            />
            <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer">
              <i className={`${showCurrent ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`} />
            </button>
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-foreground-600 mb-1.5">新密码</label>
          <div className="relative">
            <input
              type={showNew ? 'text' : 'password'}
              placeholder="新密码"
              maxLength={16}
              value={form.newPassword}
              onChange={(e) => setForm((f) => ({ ...f, newPassword: e.target.value }))}
              className="w-full px-3 py-2.5 pr-10 text-sm rounded-xl bg-background-50 border border-background-200/60 text-foreground-900 focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-300 transition-colors"
            />
            <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer">
              <i className={`${showNew ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`} />
            </button>
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-foreground-600 mb-1.5">确认新密码</label>
          <div className="relative">
            <input
              type={showConfirm ? 'text' : 'password'}
              placeholder="确认新密码"
              maxLength={16}
              value={form.confirmPassword}
              onChange={(e) => setForm((f) => ({ ...f, confirmPassword: e.target.value }))}
              className="w-full px-3 py-2.5 pr-10 text-sm rounded-xl bg-background-50 border border-background-200/60 text-foreground-900 focus:outline-none focus:border-accent-400 focus:ring-1 focus:ring-accent-300 transition-colors"
            />
            <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer">
              <i className={`${showConfirm ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`} />
            </button>
          </div>
        </div>
      </div>

      <div className="mt-6">
        {error && <p className="text-xs text-rose-600 mb-2">{error}</p>}
        <button
          onClick={handleSave}
          disabled={loading}
          className="px-5 py-2 rounded-lg bg-accent-500 text-white text-xs font-medium hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
        >
          {saved ? '已保存 ✓' : loading ? '更新中...' : '更新密码'}
        </button>
      </div>
    </div>
  );
};

export default ChangePasswordForm;
